import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { faArrowDown, faArrowLeft, faArrowUp, faBook, faFile, faFolder, faFolderOpen, faGripVertical, faMagnifyingGlass, faPencil, faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { SystemMessage, SystemMessageService } from 'app/core/system-message/system-message-service';
import { PcgFile } from 'app/shared/form-elements/file-upload/file-upload.component';
import { TextEditorComponent } from 'app/shared/form-elements/text-editor/text-editor.component';
import { KnowledgeBaseDocumentVm } from 'app/shared/generated/HelpDesk/Models/KnowledgeBaseDocumentVm';
import { KnowledgeBaseSectionVm } from 'app/shared/generated/HelpDesk/Models/KnowledgeBaseSectionVm';
import { SelectItem } from 'app/shared/models/select-item';
import { GlobalService } from 'app/shared/services/global.service';

/** Everything that varies between Knowledge Base folders. Add an entry and the nav, security, sorting and dropdowns all follow. */
export interface KnowledgeBaseModule {
	/** `KnowledgeBaseDocument.Module`. Doubles as the folder label via `BusinessAreaEnum.toDisplay()`. */
	module: BusinessAreaEnum;
	/** Area a user must administer to create, edit, delete or reorder this folder's documents. Defaults to `module`. */
	authoringArea?: BusinessAreaEnum;
	/** Minimum role within `authoringArea` that grants authoring rights. Defaults to `SystemAdmin`. */
	authoringRole?: PermissionRole;
	/** Readable by every user regardless of module access, and publishable at the 'No Access' role. */
	isPublic?: boolean;
	/** Readers are gated purely on their own role in this area — support admins get no override. */
	noSupportAdminOverride?: boolean;
	/** Offered in the Module dropdown even without authoring access. */
	alwaysSelectable?: boolean;
}

/** A rendered folder in the file nav. */
export interface KnowledgeBaseFolder {
	module: BusinessAreaEnum;
	label: string;
	docs: KnowledgeBaseDocumentVm[];
	isExpanded: boolean;
	/** Whether the current user may author in this folder. Drives the folder header's pencil. */
	canAuthor: boolean;
}

/** The single source of truth for Knowledge Base folders. Array order is nav order. */
export const knowledgeBaseModules: readonly KnowledgeBaseModule[] = [
	{ module: BusinessAreaEnum.Admin }
	, { module: BusinessAreaEnum.SysInv }
	, { module: BusinessAreaEnum.CI }
	, { module: BusinessAreaEnum.HEROS }
	, { module: BusinessAreaEnum.MetabolicFormula }
	, { module: BusinessAreaEnum.NDC }
	// PFS and Other are company-wide reading material, administered by the Admin module.
	, {
		module: BusinessAreaEnum.PFS
		, authoringArea: BusinessAreaEnum.Admin
		, isPublic: true
	}
	// Pharmacy has never been gated out of the Module dropdown.
	, {
		module: BusinessAreaEnum.Inventory
		, alwaysSelectable: true
	}
	, {
		module: BusinessAreaEnum.RxConnects
		, authoringRole: PermissionRole.RxConnectsAdmin
	}
	, {
		module: BusinessAreaEnum.HelpDesk
		, noSupportAdminOverride: true
	}
	, {
		module: BusinessAreaEnum.Other
		, authoringArea: BusinessAreaEnum.Admin
		, isPublic: true
	}
];

/** Which face the document panel is showing. */
export type KnowledgeBaseDocMode = 'view' | 'create' | 'edit';

/** Which face the create/edit form is showing. */
export type KnowledgeBaseFormTab = 'edit' | 'preview';

/** One section as the create/edit form renders it. */
export interface KnowledgeBaseSectionRow {
	fg: UntypedFormGroup;
	index: number;
	/** 1-based, for the row's visible heading. */
	position: number;
	/**
	 * The `@for` track key. Trix reads its starting markup once and ignores later input
	 * changes, so an editor that keeps its key keeps its content — which is what we want
	 * while typing, and exactly what we do not want once a row has moved. Changing the
	 * key is therefore the only way to re-seed an editor.
	 */
	editorKey: string;
	/** Seed markup for this row's editor, snapshotted whenever the rows are rebuilt. */
	content: string;
	canMoveUp: boolean;
	canMoveDown: boolean;
	/** The form always keeps one section, so the last one cannot be removed — only emptied. */
	canRemove: boolean;
}

@Component({
	selector: 'pcg-knowledge-base',
	templateUrl: './knowledge-base.component.html',
	styleUrls: ['./knowledge-base.component.scss'],
	standalone: false,
	changeDetection: ChangeDetectionStrategy.Eager,
	host: { '(window:resize)': 'onResize()' }
})
export class KnowledgeBaseComponent implements OnInit {

	private readonly httpClient = inject(HttpClient);
	private readonly ms = inject(SystemMessageService);
	private readonly sec = inject(SecurityService);

	readonly faIcon = {
		faBook, faFolder, faFolderOpen, faFile, faXmark
		, faPencil, faGripVertical, faArrowLeft, faMagnifyingGlass
		, faTrash, faArrowUp, faArrowDown
	};

	formGroup = KnowledgeBaseDocumentVm.Form;

	/** Every section's editor, in the order the sections are rendered. */
	private readonly sectionEditors = viewChildren(TextEditorComponent);

	// #region Design tokens
	/**
	 * The splitter is pure structure here — the two panels draw their own surfaces,
	 * so its root chrome stays out of the way and only the gutter is themed.
	 */
	readonly splitterDts = {
		root: {
			background: 'transparent'
			, borderColor: 'transparent'
		}
		, gutter: { background: 'rgb(235, 235, 235)', size: '0.6rem' }
		, handle: { background: '#bbb' }
	};

	/** Reads as the panel's own footer rather than a floating card. */
	readonly formActionDts = {
		root: {
			background: '#fff'
			, borderColor: 'rgba(227, 228, 228, 0.721)'
			, borderRadius: '0'
			, padding: '0.75rem 1rem'
			, gap: '0.5rem'
		}
	};
	// #endregion

	// #region Layout
	readonly isMobile = signal(GlobalService.setIsMobile(window.innerWidth));
	// #endregion

	// #region Security
	readonly isSupportAdmin = computed(() => this.hasAccess(BusinessAreaEnum.HelpDesk, PermissionRole.SystemAdmin));

	/** True when the current user may create, edit, delete or reorder the open document. */
	readonly canEdit = computed(() => this.canAuthor(this.selectedDoc()?.module));
	// #endregion

	// #region File nav
	readonly docs = signal<KnowledgeBaseDocumentVm[]>([]);
	readonly search = signal('');
	readonly expandedModules = signal<ReadonlySet<BusinessAreaEnum>>(new Set());

	private readonly readableDocs = computed(() => this.docs().filter(doc => this.canRead(doc)));

	private readonly matchingDocs = computed(() => {
		const term = this.search().trim().toLowerCase();
		if (term === '') { return this.readableDocs(); }
		return this.readableDocs().filter(doc =>
			doc?.title?.toLowerCase().includes(term)
			|| doc?.description?.toLowerCase().includes(term)
		);
	});

	/** Non-empty folders in nav order, each in the sort order the server maintains. */
	private readonly populatedFolders = computed(() => knowledgeBaseModules
		.map(config => ({
			module: config.module
			, label: BusinessAreaEnum.toDisplay(config.module)
			, docs: this.matchingDocs()
				.filter(doc => this.moduleOf(doc?.module) === config.module)
				.sort((a, b) => a.sortOrder - b.sortOrder)
		}))
		.filter(folder => folder.docs.length > 0)
	);

	readonly folders = computed<KnowledgeBaseFolder[]>(() => this.populatedFolders()
		.map(folder => ({
			...folder
			, isExpanded: this.expandedModules().has(folder.module)
			, canAuthor: this.canAuthor(folder.module)
		}))
	);

	readonly hasSearchResults = computed(() => this.search() === '' || this.folders().length > 0);

	/**
	 * Reordering renumbers a folder by list position, so it can only run against the
	 * whole folder — never a search-filtered subset.
	 */
	readonly isSearching = computed(() => this.search() !== '');
	// #endregion

	// #region Document panel
	readonly mode = signal<KnowledgeBaseDocMode>('view');
	readonly isCreateDoc = computed(() => this.mode() === 'create');
	readonly isEditDoc = computed(() => this.mode() === 'edit');
	readonly isViewingDoc = computed(() => this.mode() === 'view');

	/** Null until the reader picks something, which is what shows the empty state. */
	readonly selectedDoc = signal<KnowledgeBaseDocumentVm>(null);

	readonly formTab = signal<KnowledgeBaseFormTab>('edit');
	readonly isPreviewTab = computed(() => this.formTab() === 'preview');
	readonly formTabOptions = [
		{ label: 'Edit', value: 'edit' as KnowledgeBaseFormTab }
		, { label: 'Preview', value: 'preview' as KnowledgeBaseFormTab }
	];

	/**
	 * Snapshot of the form taken when Preview opens, so the read view can render
	 * unsaved values. Not a live projection — the form is not signal-based.
	 */
	readonly previewDoc = signal<KnowledgeBaseDocumentVm>(new KnowledgeBaseDocumentVm());

	// #region Sections
	/**
	 * Mirrors the `sections` form array, which is not signal-based. Every mutation of the
	 * array goes through this component and refreshes this, so the template has something
	 * to react to.
	 */
	private readonly sectionFgs = signal<UntypedFormGroup[]>([]);

	/**
	 * Bumped whenever a row changes position. Feeds `editorKey`, so a bump tears every
	 * editor down and rebuilds it against the section that now occupies its slot.
	 */
	private readonly editorGeneration = signal(0);

	readonly sectionRows = computed<KnowledgeBaseSectionRow[]>(() => {
		const fgs = this.sectionFgs();
		const generation = this.editorGeneration();
		return fgs.map((fg, index) => ({
			fg
			, index
			, position: index + 1
			, editorKey: `${generation}-${index}`
			, content: fg.controls.content.value ?? ''
			, canMoveUp: index > 0
			, canMoveDown: index < fgs.length - 1
			, canRemove: fgs.length > 1
		}));
	});
	// #endregion

	readonly knowledgeBaseModuleItems = signal<SelectItem[]>([]);
	readonly permissionItems = signal<SelectItem[]>([]);
	readonly canCreateDoc = computed(() => this.knowledgeBaseModuleItems().length > 0 && this.isViewingDoc());

	readonly errors = signal<string[]>([]);
	readonly isError = computed(() => this.errors().length > 0);
	readonly errorStr = computed(() => this.errors().join(' '));

	readonly isDeleteDialogOpen = signal(false);
	readonly isDeleting = signal(false);

	/** True when the reading panel has something to show — an open form or a selected document. */
	readonly hasPanelContent = computed(() => !this.isViewingDoc() || this.selectedDoc() !== null);

	/**
	 * Phones get a master-detail: the list, or the document, never both squeezed
	 * side by side. Desktop always shows the pair.
	 */
	readonly showListPanel = computed(() => !this.isMobile() || !this.hasPanelContent());
	readonly showDocPanel = computed(() => !this.isMobile() || this.hasPanelContent());
	// #endregion

	constructor() { inject(Title).setTitle('Knowledge Base'); }

	ngOnInit() {
		this.getDocuments();
		this.getModules();
	}

	onResize() {
		const isMobile = GlobalService.setIsMobile(window.innerWidth);
		if (isMobile === this.isMobile()) { return; }

		// Crossing the breakpoint swaps the splitter for the stacked layout, which tears
		// down and rebuilds the Trix editors. Bank what's typed before that happens.
		if (!this.isViewingDoc()) { this.syncEditorsToForm(); }
		this.isMobile.set(isMobile);
	}

	// #region Security
	/** Documents carry their module as a raw number, so normalize before matching it against a config. */
	private moduleOf(module: BusinessAreaEnum): BusinessAreaEnum {
		const value = Number(module);
		return Number.isFinite(value) ? value : null;
	}

	private configFor(module: BusinessAreaEnum): KnowledgeBaseModule {
		return knowledgeBaseModules.find(config => config.module === this.moduleOf(module));
	}

	private hasAccess(area: BusinessAreaEnum, minRole: PermissionRole) {
		return this.sec?.hasModuleAccess([area], SecurityService.setMinRole(minRole)) ?? false;
	}

	/** Whether the current user is allowed to see a document at all. */
	private canRead(doc: KnowledgeBaseDocumentVm) {
		const config = this.configFor(doc?.module);
		if (!config) { return false; }
		if (config.isPublic) { return true; }
		return this.hasAccess(config.module, doc.permissionRole)
			|| (!config.noSupportAdminOverride && this.isSupportAdmin());
	}

	/** Whether the current user may create, edit, delete or reorder documents in a folder. */
	canAuthor(module: BusinessAreaEnum) {
		const config = this.configFor(module);
		if (!config) { return false; }
		return this.hasAccess(
			config.authoringArea ?? config.module
			, config.authoringRole ?? PermissionRole.SystemAdmin
		) || this.isSupportAdmin();
	}
	// #endregion

	// #region Document actions
	createDocument() {
		this.formGroup.reset();
		this.formGroup.controls.documentId.setValue(0);
		// reset() leaves the section array at whatever length it was, so rebuild it outright.
		this.setSections([]);
		this.errors.set([]);
		this.formTab.set('edit');
		// Nothing is being read while creating, so cancelling lands back on the empty state.
		this.selectedDoc.set(null);
		this.mode.set('create');
	}

	/**
	 * The pencil on a folder header. Folders themselves are a fixed config array rather
	 * than editable records, so the authoring action a folder can offer is starting a new
	 * document already pointed at it.
	 */
	createDocumentInFolder(module: BusinessAreaEnum, event: MouseEvent) {
		// The pencil sits inside the folder header, which toggles the folder on click.
		event.stopPropagation();

		this.createDocument();
		this.formGroup.controls.module.setValue(this.moduleOf(module));
		this.getPermissions();
	}

	openDocument(doc: KnowledgeBaseDocumentVm) {
		this.selectedDoc.set(doc);
		this.errors.set([]);
		this.mode.set('view');
	}

	editDocument() {
		const doc = this.selectedDoc();
		// The array has to be the right length before the document is patched over it, since
		// a form array only takes values for the rows it already has.
		this.setSections(doc?.sections);
		this.formGroup.patchValue(doc);
		if (this.formGroup.controls.module.value != null) { this.getPermissions(); }
		this.errors.set([]);
		this.formTab.set('edit');
		this.mode.set('edit');
	}

	cancelDocumentEdit() {
		this.errors.set([]);
		this.formTab.set('edit');
		this.mode.set('view');
	}

	/** Clears the panel back to its empty state. On phones this is the 'back to list' action. */
	closeDocument() {
		this.formGroup = KnowledgeBaseDocumentVm.Form;
		this.sectionFgs.set([]);
		this.selectedDoc.set(null);
		this.errors.set([]);
		this.formTab.set('edit');
		this.mode.set('view');
	}

	confirmDelete() { this.isDeleteDialogOpen.set(true); }

	deleteDocument() {
		this.isDeleting.set(true);
		this.httpClient.get<SystemMessage>(
			`api/HelpDesk/KnowledgeBase/DeleteKnowledgeBaseDocument?id=${this.selectedDoc()?.documentId}`
		).subscribe(sm => {
			this.isDeleting.set(false);
			this.isDeleteDialogOpen.set(false);
			if (sm?.isSuccessful) {
				// The delete endpoint hands back the surviving documents.
				this.docs.set(sm?.value ?? []);
				this.closeDocument();
			}
			this.ms.setSystemMessage(sm?.message, sm?.messageClass);
		});
	}

	private get sectionsArray() { return this.formGroup.controls.sections as UntypedFormArray; }

	/**
	 * Trix holds its markup on the element rather than the form control, so pull it
	 * across whenever the form's value needs to be authoritative.
	 */
	private syncEditorsToForm() {
		// Editors render one per section in array order, so position pairs them up.
		this.sectionEditors().forEach((editor, index) =>
			this.sectionFgs()[index]?.controls.content.setValue(editor.value)
		);

		// Re-snapshot the seed values so a re-mount restores what was typed rather than
		// the content the row was opened with.
		this.refreshSectionRows();
	}

	/** Rebuilds the rows from saved sections. Always leaves one, so there is always somewhere to type. */
	private setSections(sections: KnowledgeBaseSectionVm[]) {
		const array = this.sectionsArray;
		array.clear();

		const source = sections?.length > 0 ? sections : [null];
		source.forEach(section => {
			const fg = KnowledgeBaseSectionVm.Form;
			if (section) { fg.patchValue(section); }
			array.push(fg);
		});

		this.rebuildEditors();
		this.refreshSectionRows();
	}

	private refreshSectionRows() { this.sectionFgs.set([...this.sectionsArray.controls] as UntypedFormGroup[]); }

	/**
	 * Forces every editor to be torn down and re-seeded from the form. Needed after anything
	 * that moves a row, because Trix ignores its input once mounted and an editor left in
	 * place would go on showing the text of whichever section used to sit there.
	 */
	private rebuildEditors() { this.editorGeneration.update(generation => generation + 1); }

	/** Appends an empty section. Leaves the editors above it alone, so nothing in progress is disturbed. */
	addSection() {
		this.syncEditorsToForm();
		this.sectionsArray.push(KnowledgeBaseSectionVm.Form);
		this.refreshSectionRows();
	}

	removeSection(index: number) {
		this.syncEditorsToForm();
		this.sectionsArray.removeAt(index);
		this.rebuildEditors();
		this.refreshSectionRows();
	}

	/** Moves a section one place through the article. `offset` is -1 for up, 1 for down. */
	moveSection(index: number, offset: number) {
		const target = index + offset;
		if (target < 0 || target >= this.sectionsArray.length) { return; }

		this.syncEditorsToForm();
		const moved = this.sectionsArray.at(index);
		this.sectionsArray.removeAt(index);
		this.sectionsArray.insert(target, moved);
		this.rebuildEditors();
		this.refreshSectionRows();
	}

	/**
	 * Preview renders through the same component as the real read view, fed a snapshot
	 * of the form. The editors stay mounted behind it, so switching tabs costs no state.
	 */
	setFormTab(tab: KnowledgeBaseFormTab) {
		if (tab === 'preview') {
			this.syncEditorsToForm();
			this.previewDoc.set(this.formGroup.getRawValue());
		}
		this.formTab.set(tab);
	}

	onSave() {
		const controls = this.formGroup.controls;
		this.errors.set([
			controls.module.value === null ? 'Module is required.' : null
			, !controls.title.value ? 'Title is required.' : null
			, controls.permissionRole.value === null ? 'Permission is required.' : null
		].filter((error): error is string => error !== null));

		if (this.isError()) { return; }

		this.syncEditorsToForm();

		this.httpClient.post<SystemMessage>(
			'api/HelpDesk/KnowledgeBase/SaveKnowledgeBaseDocument'
			, this.formGroup.getRawValue()
		).subscribe(sm => {
			if (sm?.isSuccessful) {
				this.formGroup = KnowledgeBaseDocumentVm.Form;
				this.sectionFgs.set([]);
				this.formTab.set('edit');
				this.upsertDocument(sm?.value);
				this.openDocument(sm?.value);
			}
			this.ms.setSystemMessage(sm?.message, sm?.messageClass);
		});
	}

	/**
	 * Each editor emits its own running list of files, covering only the images dropped into
	 * that section. With several editors on the form the lists have to be merged — taking the
	 * newest one wholesale would drop every image added through any other section.
	 */
	setImageInFormGroup(files: PcgFile[]) {
		this.ms.setSystemMessage(
			'Save images individually to see format. May need to refresh page after saving to view image.'
			, 'warning'
		);

		const merged = [...(this.formGroup?.controls?.uploadFiles?.value ?? [])] as PcgFile[];
		(files ?? []).forEach(file => {
			const index = merged.findIndex(o => o.fileName === file.fileName);
			if (index === -1) { merged.push(file); }
			else { merged[index] = file; }
		});
		this.formGroup?.controls?.uploadFiles?.setValue(merged);
	}

	private upsertDocument(doc: KnowledgeBaseDocumentVm) {
		if (!doc) { return; }
		this.docs.update(docs => {
			const index = docs.findIndex(o => o.documentId === doc.documentId);
			return index === -1
				? [...docs, doc]
				: docs.map((existing, i) => i === index ? doc : existing);
		});
	}
	// #endregion

	// #region File nav
	toggleFolder(module: BusinessAreaEnum) {
		this.expandedModules.update(expanded => {
			const next = new Set(expanded);
			if (!next.delete(module)) { next.add(module); }
			return next;
		});
	}

	/** Searching expands every folder so matches are visible; clearing collapses them again. */
	searchDocuments(term: string) {
		this.search.set(term);
		this.expandedModules.set(
			term === ''
				? new Set()
				: new Set(knowledgeBaseModules.map(config => config.module))
		);
	}

	reorderDocuments(event: CdkDragDrop<KnowledgeBaseDocumentVm[]>, module: BusinessAreaEnum) {
		if (!this.configFor(module)) {
			this.ms.setSystemMessage('Module is not set', 'warning');
			return;
		}
		if (!this.canAuthor(module)) { return; }
		// The endpoint renumbers by list position, so a filtered folder would clobber the
		// sortOrder of everything the search hid. The UI blocks this; this is the backstop.
		if (this.search() !== '') { return; }

		const folderDocs = this.populatedFolders().find(folder => folder.module === this.moduleOf(module))?.docs ?? [];
		const reordered = [...folderDocs];
		moveItemInArray(reordered, event.previousIndex, event.currentIndex);

		// The endpoint renumbers sortOrder by list position, so mirror that locally
		// to keep the nav from snapping back on the next search or save.
		const sorted = reordered.map((doc, i) => ({ ...doc, sortOrder: i + 1 }));
		const sortOrders = new Map(sorted.map(doc => [doc.documentId, doc.sortOrder]));
		this.docs.update(docs => docs.map(doc => sortOrders.has(doc.documentId)
			? { ...doc, sortOrder: sortOrders.get(doc.documentId) }
			: doc
		));

		this.httpClient.post<SystemMessage>('api/HelpDesk/KnowledgeBase/SortKnowledgeBaseDocuments', sorted)
			.subscribe();
	}
	// #endregion

	// #region Dropdowns
	getDocuments() {
		this.httpClient.get<KnowledgeBaseDocumentVm[]>('api/HelpDesk/KnowledgeBase/GetKnowledgeBaseDocumentList')
			.subscribe(docs => this.docs.set(docs ?? []));
	}

	getModules() {
		this.httpClient.get<SelectItem[]>('api/Select/KnowledgeBaseModules').subscribe(items => {
			this.knowledgeBaseModuleItems.set(
				(items ?? []).filter(item => {
					const config = this.configFor(item?.value);
					return !config || config.alwaysSelectable || this.canAuthor(item?.value);
				})
			);
		});
	}

	onModuleChange() {
		this.formGroup?.controls?.permissionRole?.setValue(null);
		this.permissionItems.set([]);
		if (this.formGroup?.controls?.module?.value != null) { this.getPermissions(); }
	}

	getPermissions() {
		const module = this.formGroup?.controls?.module?.value;
		this.httpClient.get<SelectItem[]>(`api/Select/PermissionRoles?module=${module}`).subscribe(items => {
			// Only public folders may be published at 'No Access'.
			this.permissionItems.set(
				this.configFor(module)?.isPublic
					? items
					: (items ?? []).filter(item => item?.value !== 0)
			);
		});
	}
	// #endregion
}
